/** What the trailing response in a chat should show, and the statuses that
 *  mean a turn ended badly. Kept apart from the renderer so the rule can be
 *  tested on its own. */
export const RETRYABLE_TASK_STATUSES = new Set(['interrupted', 'error', 'timeout']);

/** What the LAST response in a chat should show under the parts it has
 *  rendered: nothing, a working spinner, or the amber "stopped" notice.
 *
 *  Its own two flags are not enough, and that was the bug: `is_streaming`
 *  and `is_complete` reach the page in SEPARATE patches, so a response that
 *  has stopped streaming but is not yet marked complete reads as "neither"
 *  for as long as the gap between them lasts. The same gap opens between one
 *  response ending and the next beginning. Keyed on those two alone, the
 *  notice appeared for a frame or two over and over while the agent was
 *  visibly mid-turn — measured on one RFLP turn, 16 appearances in 150
 *  seconds, and reported by Tom from the dev stack.
 *
 *  So the TASK decides. While it is running or paused the turn is working,
 *  whatever a single response's flags say. A task in a retryable terminal
 *  status gets `idle` here because ChatView renders the notice for it
 *  (`TaskRetryNotice`), and the two must never double-render. */
export function trailingResponseState({
  isLastResponse,
  isResponseComplete,
  isStreaming,
  taskIsWorking,
  taskStatus,
}: {
  isLastResponse: boolean;
  isResponseComplete: boolean;
  isStreaming: boolean;
  /** The TURN is going, from ChatView (see `ChatTaskContext`). */
  taskIsWorking: boolean;
  taskStatus?: string | null;
}): 'idle' | 'working' | 'stopped' {
  if (!isLastResponse || isResponseComplete) return 'idle';
  if (isStreaming) return 'working';
  if (taskIsWorking) return 'working';
  if (taskStatus === 'running' || taskStatus === 'paused') return 'working';
  if (RETRYABLE_TASK_STATUSES.has(taskStatus ?? '')) return 'idle';
  return 'stopped';
}
